import * as React from "react";
import { ChevronDown, Info } from "lucide-react";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Collapsible } from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import type { MarketDataAttribution } from "../../types/marketDataAttribution";
import { resolveMarketDataDisclaimer } from "../../utils/marketDataAttributionCopy";

export type MarketDataAttributionNoticeProps = {
  attribution?: MarketDataAttribution | null;
  sourceLabel?: string | null;
  className?: string;
};

export function MarketDataAttributionNotice({
  attribution,
  sourceLabel,
  className,
}: MarketDataAttributionNoticeProps): React.ReactElement | null {
  const [expanded, setExpanded] = React.useState(false);
  const copy = resolveMarketDataDisclaimer(attribution, sourceLabel);

  const toggleClassName =
    "inline-flex h-auto min-h-0 shrink-0 items-center justify-end gap-1 p-0 text-sm font-normal leading-5 no-underline hover:bg-transparent";
  const toggleStyle: React.CSSProperties = {
    color: "var(--brand-tropical-surf)",
    height: "auto",
    paddingInline: 0,
    fontSize: "0.875rem",
    lineHeight: "1.25rem",
    fontWeight: 400,
  };

  if (!attribution && !sourceLabel?.trim()) {
    return null;
  }

  return (
    <Alert
      variant="info"
      data-market-data-attribution=""
      className={cn(
        "grid w-full grid-cols-1 gap-2 rounded-lg font-[family-name:var(--font-primary)] md:grid-cols-[auto_1fr] md:gap-x-2",
        className
      )}
    >
      <div className="flex w-full items-center justify-between gap-2 md:contents">
        <Info
          aria-hidden
          className="size-4 shrink-0 text-[color:var(--brand-tropical-surf)] md:row-span-2 md:translate-y-0.5"
        />
        <Button
          type="button"
          variant="link"
          size="sm"
          data-font="primary"
          aria-expanded={expanded}
          className={cn(toggleClassName, "md:hidden")}
          style={toggleStyle}
          onClick={() => setExpanded((prev) => !prev)}
        >
          {expanded ? "Show less" : "Show more"}
          <ChevronDown
            aria-hidden
            className={cn(
              "!size-3.5 shrink-0 transition-transform duration-200",
              expanded && "rotate-180"
            )}
          />
        </Button>
      </div>

      <Collapsible
        open={expanded}
        onOpenChange={setExpanded}
        className="relative min-w-0 w-full md:col-start-2"
      >
        <AlertDescription className="pr-[6.75rem] font-[family-name:var(--font-primary)] text-sm font-normal leading-5 text-gray-500 dark:text-white">
          {expanded ? copy.expanded : `${copy.shortLabel}. ${copy.collapsed}`}
        </AlertDescription>
        <Button
          type="button"
          variant="link"
          size="sm"
          data-font="primary"
          aria-expanded={expanded}
          className={cn(toggleClassName, "absolute top-0 right-0 hidden w-[6.75rem] md:inline-flex")}
          style={toggleStyle}
          onClick={() => setExpanded((prev) => !prev)}
        >
          {expanded ? "Show less" : "Show more"}
          <ChevronDown
            aria-hidden
            className={cn(
              "!size-3.5 shrink-0 transition-transform duration-200",
              expanded && "rotate-180"
            )}
          />
        </Button>
      </Collapsible>
    </Alert>
  );
}
